import React, { useEffect, useState } from 'react';
import { getAllPhotosWithUrls } from '../../services/photoMetadataService';
import { ImageData } from '../../types/gallery';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG ImageLoader] Component loaded');

interface ImageLoaderProps {
  onImagesLoaded: (images: ImageData[]) => void;
}

const ImageLoader: React.FC<ImageLoaderProps> = ({ onImagesLoaded }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadImages = async () => {
      try {
        setLoading(true);
        setError(null);

        debugLog('[DEBUG ImageLoader] Fetching photos from API...');
        const photos = await getAllPhotosWithUrls(false); // Only visible photos
        debugLog('[DEBUG ImageLoader] Retrieved', photos.length, 'photos');

        // Transform Photo objects to ImageData format
        const images: ImageData[] = photos.map(photo => ({
          key: photo.s3Key,
          url: photo.url,
          thumbnailUrl: photo.thumbnailUrl,
          lastModified: new Date(photo.uploadedAt),
          size: photo.fileSize,
          name: photo.filename,
          caption: photo.caption,
          photoId: photo.photoId
        }));

        // Sort images by order (from DynamoDB)
        const sortedImages = images.sort((a, b) => {
          const photoA = photos.find(p => p.photoId === a.photoId);
          const photoB = photos.find(p => p.photoId === b.photoId);
          return (photoA?.order || 0) - (photoB?.order || 0);
        });

        debugLog('[DEBUG ImageLoader] Loaded', sortedImages.length, 'images');
        onImagesLoaded(sortedImages);

      } catch (err) {
        console.error('Failed to load images:', err);
        setError('Failed to load gallery images');
        // Still provide empty array so component doesn't break
        onImagesLoaded([]);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, [onImagesLoaded]);

  // This component doesn't render anything visible
  if (loading) {
    return null;
  }

  if (error) {
    console.warn('ImageLoader error:', error);
    return null;
  }

  return null;
};

export default ImageLoader;